/**
 * Every icon/image in the dashboard is an asset exported from Figma and committed
 * under `public/assets/dashboard`. Re-export from Figma and re-run
 * `scripts/fetch-dashboard-assets.ps1` if the design changes.
 */
const BASE = '/assets/dashboard'

/** Icons shared by the collapsed rail and the expanded panel. */
export const navIcons = {
  dashboard: `${BASE}/nav-dashboard.svg`,
  myBookings: `${BASE}/nav-my-bookings.png`,
  services: `${BASE}/nav-services.png`,
  categoriesBrands: `${BASE}/nav-cat_brand.png`,
  driver: `${BASE}/nav-driver.png`,
  calendar: `${BASE}/nav-calendar.png`,
  payments: `${BASE}/nav-payments.png`,
  enquiries: `${BASE}/nav-feedback.png`,
  feedback: `${BASE}/nav-feedback.png`,
  profileSettings: `${BASE}/nav-profile-settings.png`,
  notifications: `${BASE}/nav-notifications.png`,
  notificationDot: `${BASE}/nav-notification-dot.svg`,
  logout: `${BASE}/nav-logout.svg`,
} as const

/**
 * Variants the collapsed rail uses in place of the shared icons: a filled dashboard
 * mark for the active chip, plus its own calendar and profile glyphs.
 */
export const railIcons = {
  dashboard: `${BASE}/rail-dashboard.svg`,
  calendar: `${BASE}/rail-calendar.svg`,
  profile: `${BASE}/rail-profile.png`,
} as const

export const shellAssets = {
  sidebarToggle: `${BASE}/sidebar-toggle.svg`,
  logo: `${BASE}/logo.png`,
  titleIcon: `${BASE}/topbar-title-icon.png`,
  search: `${BASE}/topbar-search.png`,
  avatar: `${BASE}/topbar-avatar.png`,
} as const

export const statAssets = {
  bookings: `${BASE}/stat-bookings.svg`,
  tripsToday: `${BASE}/stat-trips-today.svg`,
  clockFace: `${BASE}/stat-clock-face.svg`,
  clockHands: `${BASE}/stat-clock-hands.svg`,
  revenue: `${BASE}/stat-revenue.svg`,
  vehicles: `${BASE}/stat-vehicles.svg`,
  openArrow: `${BASE}/stat-open-arrow.svg`,
  trendArrowFront: `${BASE}/trend-arrow-front.svg`,
  trendArrowBack: `${BASE}/trend-arrow-back.svg`,
} as const

export const chartAssets = {
  gridLine: `${BASE}/chart-grid-line.svg`,
  axisLine: `${BASE}/chart-axis-line.svg`,
  line: `${BASE}/chart-line.svg`,
  dotAxisDim: `${BASE}/chart-dot-axis-dim.svg`,
  dotAxisLit: `${BASE}/chart-dot-axis-lit.svg`,
  dotLine: `${BASE}/chart-dot-line.svg`,
  dotLineAlt: `${BASE}/chart-dot-line-alt.svg`,
  tooltip: `${BASE}/chart-tooltip.svg`,
  rangeCaret: `${BASE}/chart-range-caret.svg`,
} as const

export const donutAssets = {
  ring: `${BASE}/donut.svg`,
  leaderSki: `${BASE}/donut-leader-ski.svg`,
  leaderDay: `${BASE}/donut-leader-day.svg`,
  leaderAirport: `${BASE}/donut-leader-airport.svg`,
  leaderOthers: `${BASE}/donut-leader-others.svg`,
  leaderPrivate: `${BASE}/donut-leader-private.svg`,
} as const

export const bookingAssets = {
  thumb: `${BASE}/booking-thumb.png`,
  calendar: `${BASE}/booking-calendar.png`,
  separatorDot: `${BASE}/booking-dot.svg`,
} as const

/** Shared controls: selects, search, buttons and the breadcrumb separator. */
export const controlIcons = {
  selectCaret: `${BASE}/select-caret.png`,
  breadcrumbArrow: `${BASE}/breadcrumb-arrow.svg`,
  search: `${BASE}/search-line.svg`,
  add: `${BASE}/add-white.svg`,
  filter: `${BASE}/filter.svg`,
} as const

/** Cell decorations inside the bookings table. */
export const tableIcons = {
  routeRing: `${BASE}/route-ring.svg`,
  routeArrow: `${BASE}/route-arrow.svg`,
  driverTick: `${BASE}/driver-tick.svg`,
  driverAdd: `${BASE}/driver-add.svg`,
  rowActions: `${BASE}/row-actions.svg`,
  statusCaretSm: `${BASE}/status-caret-sm.svg`,
} as const

/** Driver card imagery and glyphs. */
export const driverAssets = {
  avatar: `${BASE}/driver-avatar.png`,
  onlineDot: `${BASE}/driver-online-dot.svg`,
  phone: `${BASE}/driver-phone.svg`,
  plateCar: `${BASE}/driver-plate-car.svg`,
  eye: `${BASE}/driver-eye.svg`,
} as const

/** Category tabs, card decorations and vehicle glyphs on the Services page. */
export const serviceAssets = {
  filterAll: `${BASE}/service-filter-all.png`,
  filterAirport: `${BASE}/service-filter-airport.svg`,
  filterHotelA: `${BASE}/service-filter-hotel-a.png`,
  filterHotelB: `${BASE}/service-filter-hotel-b.png`,
  filterSki: `${BASE}/service-filter-ski.svg`,
  filterDayTrips: `${BASE}/service-filter-day-trips.svg`,
  filterPrivateVan: `${BASE}/service-filter-private-van.svg`,
  filterFleets: `${BASE}/service-filter-fleets.svg`,
  edit: `${BASE}/service-edit.svg`,
  vehicleCar: `${BASE}/service-vehicle-car.svg`,
  vehicleVan: `${BASE}/service-vehicle-van.svg`,
  vehicleBus: `${BASE}/service-vehicle-bus.svg`,
  createPlus: `${BASE}/service-create-plus.svg`,
  route: `${BASE}/service-route.svg`,
  view: `${BASE}/service-view.svg`,
} as const

export const currencyIcons = {
  euroWhite: `${BASE}/euro-white.svg`,
  euroBlack: `${BASE}/euro-black.svg`,
} as const

/** Icons unique to the Payments page table, toolbar and details panel. */
export const paymentAssets = {
  visa: `${BASE}/payment-visa.svg`,
  paypal: `${BASE}/payment-paypal.svg`,
  copy: `${BASE}/payment-copy.svg`,
  download: `${BASE}/payment-download.svg`,
  refund: `${BASE}/payment-refund.svg`,
  send: `${BASE}/payment-send.svg`,
  calendar: `${BASE}/payment-calendar.svg`,
  successDot: `${BASE}/payment-success-dot.svg`,
} as const

/** Icons unique to the Feedback / FAQ tabs. */
export const feedbackAssets = {
  star: `${BASE}/feedback-star.svg`,
  edit: `${BASE}/faq-edit.svg`,
} as const

export const notificationAssets = {
  main: `${BASE}/notifications/notification-main.png`,
  total: `${BASE}/notifications/notification1.png`,
  unread: `${BASE}/notifications/notification2.png`,
  today: `${BASE}/notifications/notification3.png`,
  filter: `${BASE}/notifications/rivet-icons_filter.png`,
  sideBox1: `${BASE}/notifications/side-box-1.png`,
  sideBox2: `${BASE}/notifications/side-box-2.png`,
  sideBox3: `${BASE}/notifications/side-box-3.png`,
  sideBox4: `${BASE}/notifications/side-box-4.png`,
  sideBox5: `${BASE}/notifications/side-box-5.png`,
  sideBox6: `${BASE}/notifications/side-box-6.png`,
  sideBox7: `${BASE}/notifications/side-box-7.png`,
  sideBox8: `${BASE}/notifications/side-box-8.png`,
  sideBox9: `${BASE}/notifications/side-box-9.png`,
} as const

